import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Empty, Segmented, Select, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import "./threeM.css";
import {
  PhasingIndex,
  ThreeMMeasure,
  ThreeMPhaseNode,
  PhasedAmounts,
  ThreeMTimePhasingData,
  TreeRow,
  aggWorkBasis,
  allExpandableKeys,
  buildTree,
  fmtINR,
  fmtINRCompact,
  fmtQty,
  getStatusColor,
  perUnit,
  normalizeRollup,
  sumBoqValue,
  threeMRowClassName,
} from "./threeMShared";
import { MUTED, boqBasisLine } from "./threeMCells";
import { useWbsTreeName } from "../../shared/useWbsCodes";
import MonthBreakdownDrawer from "./MonthBreakdownDrawer";
import { buildFyOptions, fmtMonth, fyOf } from "../MonthlyPnl/pnlShared";
import { useFontScale } from "../../../../theme/fontScale";

// Time Phasing tab of the 3M Consolidation Report: the same Phase → Activity →
// Task tree, but every column is a calendar month. Each cell is the node's 3M
// cost scaled by that month's share of its tasks (scheduledQty / workQuantity),
// which makes this the project's cash-flow curve broken down by WBS — the view
// Monthly P&L cannot give, because it aggregates by cost head, not by WBS.
//
// One financial year at a time: the full range runs to 111 months on a live
// project, which is unreadable on screen. The Excel sheet carries every month.

const MEASURES: Array<{ label: string; value: ThreeMMeasure }> = [
  { label: "Total", value: "total" },
  { label: "Material", value: "material" },
  { label: "Manpower", value: "manpower" },
  { label: "Machinery", value: "machinery" },
];

const TimePhasingTree: React.FC<{
  phases: ThreeMPhaseNode[];
  phasing: ThreeMTimePhasingData | null;
  index: PhasingIndex;
  onViewTask: (taskId: number, taskName: string) => void;
  // Passed straight to the month drawer, which fetches the itemised detail
  // with the SAME filters the grid was loaded with.
  projectId?: number | string;
  phaseId?: number | string;
  activityId?: number | string;
  projectName?: string;
  orgName?: string;
  orgLogoUrl?: string;
  filtersText?: string;
  getContainer?: () => HTMLElement;
}> = ({
  phases,
  phasing,
  index,
  onViewTask,
  projectId,
  phaseId,
  activityId,
  projectName,
  orgName,
  orgLogoUrl,
  filtersText,
  getContainer,
}) => {
  const { fs, scale } = useFontScale();
  const w = (base: number) => Math.round(base * scale);

  const [measure, setMeasure] = useState<ThreeMMeasure>("total");
  const [fy, setFy] = useState<number | null>(null);
  // Month whose three-M composition is open; null = drawer closed.
  const [breakdownMonth, setBreakdownMonth] = useState<string | null>(null);

  const treeData = useMemo(() => buildTree(phases), [phases]);
  // Phase/activity names embed a WBS code; task names are already clean.
  const treeLabel = useWbsTreeName(treeData);
  const expandKeys = useMemo(() => allExpandableKeys(treeData), [treeData]);

  // Several phases run concurrently in most months, so the number a planner
  // actually reads first is the month's total ACROSS phases. It leads the grid
  // rather than trailing it: as a Table.Summary it rendered after every task
  // row, and because project.css gives these tables no scroll container of
  // their own it could not pin either — on this project that put the monthly
  // total ~1,000 rows below the fold. Same order as the Excel sheet.
  const rows = useMemo<TreeRow[]>(
    () => [
      {
        key: "PRJ",
        id: 0,
        name: "Project Total",
        rollup: normalizeRollup(undefined),
        // Project work basis on the same rule the tree uses for parents: a
        // quantity only when EVERY task shares one UOM, so a project mixing
        // m³ with KWh quotes no project rate rather than a meaningless one.
        // The BOQ value has no such limit — rupees always sum.
        ...aggWorkBasis(
          (phases || []).flatMap((phase) =>
            (phase.activities || []).flatMap((activity) => activity.tasks || [])
          )
        ),
        boqValue: sumBoqValue(
          (phases || []).flatMap((phase) =>
            (phase.activities || []).flatMap((activity) => activity.tasks || [])
          )
        ),
      },
      ...treeData,
    ],
    [treeData, phases]
  );

  const fyOptions = useMemo(
    () => buildFyOptions(phasing?.firstMonth, phasing?.lastMonth),
    [phasing?.firstMonth, phasing?.lastMonth],
  );

  // Default to today's FY only when the schedule actually covers it; otherwise
  // open on the first scheduled FY. Monthly P&L defaults blindly to the current
  // FY, which opens empty on a project that has finished or not yet started.
  useEffect(() => {
    if (!fyOptions.length) {
      setFy(null);
      return;
    }
    const years = fyOptions.map((option) => fyOf(option.fromMonth));
    const current = fyOf(dayjs().format("YYYY-MM-01"));
    setFy(years.includes(current) ? current : years[0]);
  }, [fyOptions]);

  // The 12 months of the selected FY, always all 12 so one FY reads against
  // another. Months outside the project's own range render muted, so "outside
  // the project" looks different from "scheduled but zero".
  const months = useMemo(() => {
    if (fy == null) return [] as string[];
    return Array.from({ length: 12 }, (_, i) =>
      dayjs(`${fy}-04-01`).add(i, "month").format("YYYY-MM-01"),
    );
  }, [fy]);

  const inRange = (month: string) =>
    !!phasing?.firstMonth &&
    !!phasing?.lastMonth &&
    month >= phasing.firstMonth &&
    month <= phasing.lastMonth;

  const cellValue = (row: TreeRow, month: string): number => {
    const node = index.byKey.get(row.key);
    const amounts = node?.months.get(month);
    return amounts ? amounts[measure] : 0;
  };

  // Rate for the selected measure in a given month. Shown only where the node
  // has a single UOM — buildTree leaves workUom empty on mixed-UOM parents,
  // and summing quantities across units would invent a number.
  const rateLine = (row: TreeRow, cost: number, quantity: number) => {
    if (!row.workUom || cost <= 0) return null;
    const rate = perUnit(cost, quantity);
    if (rate === null) return null;
    return (
      <div
        style={{
          fontSize: fs(11),
          color: "#8094ae",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtINR(rate)}/{row.workUom}
      </div>
    );
  };

  // Muted quantity sub-line ("1,02,137 Cubic Meter"). Same UOM gate as the
  // rate: a node with no single UOM has no quantity worth printing.
  const qtyLine = (row: TreeRow, quantity: number) => {
    if (!row.workUom || !(quantity > 0)) return null;
    return (
      <div
        style={{
          fontSize: fs(11),
          color: "#8094ae",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {fmtQty(quantity)} {row.workUom}
      </div>
    );
  };

  const measureLabel =
    MEASURES.find((option) => option.value === measure)?.label ?? "Total";

  // Hover detail behind every amount: the exact cost, the scheduled output it
  // was phased against, and the resulting rate — the three numbers a planner
  // reconciles a month against. `extra` carries the all-months context on the
  // FY total, where "total" otherwise reads as the whole project.
  const cellDetail = (
    row: TreeRow,
    heading: string,
    cost: number,
    quantity: number,
    extra?: React.ReactNode
  ) => {
    const rate = perUnit(cost, quantity);
    return (
      <div
        style={{
          fontSize: fs(12),
          lineHeight: 1.7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{heading}</div>
        <div>
          {measureLabel} cost: {fmtINR(cost)}
        </div>
        {row.workUom ? (
          <>
            <div>
              Scheduled qty: {fmtQty(quantity)} {row.workUom}
            </div>
            <div>
              Rate: {rate === null ? "—" : `${fmtINR(rate)}/${row.workUom}`}
            </div>
          </>
        ) : (
          <div style={{ opacity: 0.75 }}>
            Tasks here use different UOMs — no single quantity or rate.
          </div>
        )}
        {extra}
      </div>
    );
  };

  // Compact amount carrying `detail` on hover. Replaces compactAmountCell on
  // this tab: that helper's own tooltip shows the exact ₹ only, and nesting a
  // second Tooltip around it would fight over the same hover target.
  const amountWithDetail = (
    value: number,
    detail: React.ReactNode,
    bold = false
  ) => (
    <Tooltip title={detail}>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          fontWeight: bold ? 600 : undefined,
          color: value > 0 ? undefined : MUTED,
          cursor: "help",
        }}
      >
        {value > 0 ? fmtINRCompact(value) : "₹0"}
      </span>
    </Tooltip>
  );

  // A task's rate is the same in every month by construction — the month's
  // cost is its cost x (month qty / work qty), so dividing back by the month
  // qty cancels to the task rate. Repeating that identical figure down twelve
  // columns is noise, so per-month rates render only where they actually move,
  // which happens on parents whose task mix changes month to month.
  const rateVaries = (row: TreeRow): boolean => {
    if (!row.workUom) return false;
    const node = index.byKey.get(row.key);
    if (!node) return false;
    let first: number | null = null;
    for (const month of months) {
      const a = node.months.get(month);
      if (!a || a[measure] <= 0 || a.quantity <= 0) continue;
      const rate = a[measure] / a.quantity;
      if (first === null) first = rate;
      else if (Math.abs(rate - first) > 0.005) return true;
    }
    return false;
  };

  const rowTotal = (row: TreeRow): number =>
    months.reduce((sum, month) => sum + cellValue(row, month), 0);

  const nameColumn: ColumnsType<TreeRow>[number] = {
    title: "Phase / Activity / Task",
    dataIndex: "name",
    key: "name",
    width: w(300),
    fixed: "left",
    render: (text: string, record: TreeRow) => {
      const key = record.key || "";
      const statusTag = record.status ? (
        <Tag
          color={getStatusColor(record.status)}
          style={{ fontSize: fs(10.5), lineHeight: 1.5, marginLeft: 6 }}
        >
          {record.status}
        </Tag>
      ) : null;
      // Block-level flex column so wrapped lines sit beside the floated tree
      // indent rather than under it — see threeM.css.
      const cellBox: React.CSSProperties = {
        display: "flex",
        flexDirection: "column",
        gap: 2,
        textAlign: "left",
        minWidth: 0,
      };
      if (key === "PRJ") {
        return (
          <div style={cellBox}>
            <span
              style={{ fontWeight: 700, fontSize: fs(13.5), color: "#022c57" }}
            >
              {text}
              <span
                style={{ fontWeight: 400, fontSize: fs(11), marginLeft: 6, color: "#8c8c8c" }}
              >
                all phases
              </span>
            </span>
          </div>
        );
      }
      if (key.startsWith("P-")) {
        return (
          <div style={cellBox}>
            <span
              style={{ fontWeight: 700, fontSize: fs(13.5), color: "#1f1f1f" }}
            >
              {treeLabel(key, text)}
              {statusTag}
            </span>
            {boqBasisLine(record, fs, { prefix: "Σ " })}
          </div>
        );
      }
      if (key.startsWith("A-")) {
        return (
          <div style={cellBox}>
            <span
              style={{ fontWeight: 600, fontSize: fs(12.5), color: "#262626" }}
            >
              {treeLabel(key, text)}
              {statusTag}
            </span>
            {boqBasisLine(record, fs, { prefix: "Σ " })}
          </div>
        );
      }
      return (
        <div style={cellBox}>
          {record.sequenceId ? (
            <span style={{ fontSize: fs(11), color: "#8c8c8c" }}>
              {record.sequenceId}
            </span>
          ) : null}
          <span style={{ fontSize: fs(12.5), color: "#262626" }}>
            <span
              className="inlineTableCellLink"
              onClick={() => onViewTask(record.id, record.name)}
            >
              {text}
            </span>
            {statusTag}
          </span>
          {/* Quantity @ BOQ rate = value, under the task name. */}
          {boqBasisLine(record, fs)}
        </div>
      );
    },
  };

  const columns: ColumnsType<TreeRow> = [
    nameColumn,
    ...months.map((month) => ({
      title: fmtMonth(month),
      key: month,
      width: w(120),
      align: "right" as const,
      render: (_: any, record: TreeRow) => {
        const value = cellValue(record, month);
        if (value === 0 && !inRange(month)) {
          return <span style={{ color: MUTED }}>—</span>;
        }
        // The monthly totals open that month's Material / Manpower / Machinery
        // split — the one thing the single-measure grid cannot show.
        const qty = index.byKey.get(record.key)?.months.get(month)?.quantity ?? 0;
        const detail = cellDetail(record, fmtMonth(month), value, qty);
        if (record.key === "PRJ" && value > 0) {
          return (
            <Tooltip
              title={
                <>
                  {detail}
                  <div style={{ marginTop: 6, opacity: 0.85 }}>
                    Click for the Material / Manpower / Machinery split.
                  </div>
                </>
              }
            >
              <Button
                type="link"
                size="small"
                onClick={() => setBreakdownMonth(month)}
                style={{
                  padding: 0,
                  height: "auto",
                  fontWeight: 700,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {fmtINRCompact(value)}
              </Button>
            </Tooltip>
          );
        }
        return (
          <>
            {amountWithDetail(value, detail)}
            {rateVaries(record) ? rateLine(record, value, qty) : null}
          </>
        );
      },
    })),
    {
      // Amount + the quantity behind it + the weighted average rate
      // (Σ cost ÷ Σ qty across the displayed months — weighted by each month's
      // own output, so a heavy month pulls the average toward its rate).
      title: "FY Total",
      key: "fyTotal",
      width: w(180),
      align: "right" as const,
      fixed: "right",
      onCell: () => ({ style: { background: "#f7f9fc" } }),
      render: (_: any, record: TreeRow) => {
        const node = index.byKey.get(record.key);
        const fyQty = months.reduce(
          (sum, month) => sum + (node?.months.get(month)?.quantity ?? 0),
          0
        );
        const total = rowTotal(record);
        // All-months context: the FY on screen is rarely the whole programme,
        // and a rate read out of one FY invites being quoted as the project's.
        const allCost = node?.scheduled[measure] ?? 0;
        const allQty = node?.scheduled.quantity ?? 0;
        const allRate = perUnit(allCost, allQty);
        const extra =
          allCost > 0 && Math.abs(allCost - total) > 0.005 ? (
            <div style={{ marginTop: 6, opacity: 0.85 }}>
              <div>All scheduled months: {fmtINR(allCost)}</div>
              {record.workUom ? (
                <div>
                  {fmtQty(allQty)} {record.workUom}
                  {allRate === null
                    ? ""
                    : ` · ${fmtINR(allRate)}/${record.workUom}`}
                </div>
              ) : null}
            </div>
          ) : null;
        return (
          <>
            {amountWithDetail(
              total,
              cellDetail(
                record,
                fy == null ? "FY total" : `FY ${fy}-${String((fy + 1) % 100).padStart(2, "0")} total`,
                total,
                fyQty,
                extra
              ),
              true
            )}
            {qtyLine(record, fyQty)}
            {rateLine(record, total, fyQty)}
          </>
        );
      },
    },
  ];

  // Every month column is the SAME rounded w(120), so this product is exact.
  // If a month width ever varies it must become a reduce over the rendered
  // widths — w(total) comes out short at A+/A++ and desyncs the frozen column.
  const scrollX = w(300) + w(120) * months.length + w(180);

  const [expandedRowKeys, setExpandedRowKeys] = useState<readonly React.Key[]>(
    [],
  );
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [treeData]);

  // The page-level guard only covers "no phases". The common case here is
  // phases exist but nothing is scheduled — say so instead of drawing a grid
  // of dashes.
  if (!phasing?.cells?.length) {
    return (
      <Empty
        style={{ padding: "48px 0" }}
        description="No monthly schedule for this scope — phase task quantities under Task Schedules to see the time-phased view"
      />
    );
  }

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 8 }}
        message="How these months are derived"
        description="Each month is the node's 3M cost scaled by that month's share of its tasks (scheduled quantity ÷ work quantity). Hover any amount for that month's scheduled quantity, UOM and ₹ per unit; the FY Total column carries the quantity and the weighted average rate. Tasks with no schedule, or with no work quantity to phase against, contribute nothing — the Sched % column on the 3M Breakdown tab flags them. The financial year changes only what is displayed; the Excel export carries every scheduled month."
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Segmented
          size="small"
          value={measure}
          onChange={(value) => setMeasure(value as ThreeMMeasure)}
          options={MEASURES}
        />
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <Button
            type="link"
            size="small"
            disabled={expandKeys.length === 0}
            onClick={() => setExpandedRowKeys(expandKeys)}
          >
            Expand all
          </Button>
          <Button
            type="link"
            size="small"
            disabled={expandedRowKeys.length === 0}
            onClick={() => setExpandedRowKeys([])}
          >
            Collapse all
          </Button>
          <Select
            size="small"
            style={{ minWidth: 150 }}
            value={fy ?? undefined}
            onChange={(value) => setFy(value)}
            options={fyOptions.map((option) => ({
              label: option.label,
              value: fyOf(option.fromMonth),
            }))}
          />
        </span>
      </div>

      <Table<TreeRow>
        className="customTable taskHierarchyTable threeMTree"
        columns={columns}
        dataSource={rows}
        pagination={false}
        bordered
        size="middle"
        sticky={{ offsetHeader: 0 }}
        scroll={{ x: scrollX }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
        }}
        rowClassName={(record) =>
          record.key === "PRJ"
            ? "pms-phasing-total-row"
            : threeMRowClassName(record)
        }
      />

      <MonthBreakdownDrawer
        open={!!breakdownMonth}
        month={breakdownMonth}
        amounts={
          breakdownMonth
            ? ((index.byKey.get("PRJ")?.months.get(breakdownMonth) ??
                null) as PhasedAmounts | null)
            : null
        }
        programmeTotal={index.byKey.get("PRJ")?.scheduled.total ?? 0}
        projectId={projectId}
        phaseId={phaseId}
        activityId={activityId}
        projectName={projectName}
        orgName={orgName}
        orgLogoUrl={orgLogoUrl}
        filtersText={filtersText}
        onClose={() => setBreakdownMonth(null)}
        getContainer={getContainer}
      />
    </>
  );
};

export default TimePhasingTree;
